import type { JSX } from 'react';

/**
 * The glyphs the ordering controls draw, as geometry rather than as text.
 *
 * A "+" typed as a character is not centred in its own box: the plus sign sits
 * on the font's math axis, above the middle of the em box, so a round 44px
 * button centring its line box still printed the glyph high. Every attempt to
 * correct that with a margin is a per-font guess that breaks on the next font
 * stack. These draw the stroke at the centre of a square viewBox instead, so
 * the mark is centred by construction in any box, at any size, in any font.
 *
 * They are decorative by design - `role="presentation"`, `aria-hidden`, the same
 * shape `packages/ui`'s own mark uses when it is unlabelled. Every control that
 * carries one states its own `aria-label` naming the item, because "plus"
 * repeated eleven times down a menu is not navigation (NFR-005). The attributes
 * are written out on each element rather than spread from a shared object: a
 * spread is invisible to the accessibility lint, and this is exactly the rule
 * worth having checked.
 */

export function PlusIcon({ size = 20 }: { size?: number }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 5.5v13M5.5 12h13" />
    </svg>
  );
}

export function MinusIcon({ size = 20 }: { size?: number }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M5.5 12h13" />
    </svg>
  );
}

/**
 * The basket bar's cart. It was a colour emoji, which the phone renders in the
 * emoji font's own dark palette - invisible on the dark button it sits on. A
 * stroked mark takes the colour it is given, so it is white where the bar
 * declares white and needs no second thought when the button changes.
 */
export function CartIcon({ size = 20 }: { size?: number }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2.75 4h2.1l2.3 9.6a1.6 1.6 0 0 0 1.55 1.2h7.1a1.6 1.6 0 0 0 1.55-1.2L19.6 7.2H5.6" />
      <circle cx="9.5" cy="19" r="1.5" />
      <circle cx="16.5" cy="19" r="1.5" />
    </svg>
  );
}

/**
 * The app bar's support mark: a question mark in a circle (founder,
 * 2026-09-18), which is what a traveller reads as "help" with no words beside
 * it. It replaced the chat bubble within hours of shipping: a bubble says
 * "messages", and the page behind it also offers a number to ring and an
 * address. The PRINTED card keeps its bubble (WEB-021) - there the mark sits
 * beside the words "Need help?", which do the telling.
 *
 * Stroked, like the cart and the steppers, so it takes the bar's ink.
 */
export function SupportIcon({ size = 22 }: { size?: number }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-2.9 2.6-2.9 4" />
      <path d="M12 17.5h.01" />
    </svg>
  );
}

/**
 * The language switch's two flags (GST-043), 3:2, drawn rather than typed: a
 * flag EMOJI is a pair of letters on Windows and whatever the phone's emoji
 * font decides elsewhere. English is the UNION FLAG (founder, 2026-09-18) - it
 * is what a traveller of any nationality reads as "English" on a language
 * control. That is a choice about the PICTURE only: the English behind it
 * stays American English (CLAUDE.md), and no string changes with the flag.
 *
 * The red diagonals are counterchanged - each arm sits off-centre in its white
 * one, turning like a pinwheel - because that offset is what makes the mark
 * the Union Flag rather than a red X on a white X. It is done by clipping each
 * red stroke to one side of its own centre line.
 */
export function FlagIcon({
  locale,
  width = 27,
}: {
  locale: 'en' | 'fr';
  width?: number;
}): JSX.Element {
  const height = (width * 2) / 3;
  if (locale === 'fr') {
    return (
      <svg
        className="gw-flag"
        width={width}
        height={height}
        viewBox="0 0 30 20"
        role="presentation"
        aria-hidden="true"
        focusable="false"
      >
        <path fill="#0055a4" d="M0 0h10v20H0z" />
        <path fill="#ffffff" d="M10 0h10v20H10z" />
        <path fill="#ef4135" d="M20 0h10v20H20z" />
      </svg>
    );
  }
  return (
    <svg
      className="gw-flag"
      width={width}
      height={height}
      viewBox="0 0 30 20"
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      <clipPath id="gw-flag-en-turn">
        <path d="M15 10h15v10zv10H0zH0V0zV0h15z" />
      </clipPath>
      <path fill="#012169" d="M0 0h30v20H0z" />
      <path stroke="#ffffff" strokeWidth={4} d="M0 0l30 20M30 0L0 20" />
      <path
        stroke="#c8102e"
        strokeWidth={2.67}
        clipPath="url(#gw-flag-en-turn)"
        d="M0 0l30 20M30 0L0 20"
      />
      <path stroke="#ffffff" strokeWidth={6.67} d="M15 0v20M0 10h30" />
      <path stroke="#c8102e" strokeWidth={4} d="M15 0v20M0 10h30" />
    </svg>
  );
}
